import React, { useMemo } from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import type { PublicScanBrandVisibility } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import BrandVisibilityBarChart from '../chart/brand-visibility-bar-chart/BrandVisibilityBarChart';
import { brandColorOf } from './brandColors';
import { brandLabel } from './brandLabel';
import { formatRank } from './formatRank';
import './ReportCompetitiveRanking.scss';

interface ReportCompetitiveRankingProps {
  brands: PublicScanBrandVisibility[];
  /** Wspólna dla całego raportu mapa kolorów marek. */
  brandColors: Map<string, string>;
  /** Raport zrzuca z tej instancji obraz wykresu do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
}

/**
 * Ten sam wykres, co „Visibility" na dashboardzie V2 — komponent jest współdzielony, więc raport i
 * panel nie rozjadą się przy kolejnej zmianie. Sam wykres tnie listę do TOP 7 i zawsze dokłada
 * badaną markę.
 */
export const ReportCompetitiveRanking: React.FC<ReportCompetitiveRankingProps> = ({
  brands,
  brandColors,
  chartRef,
}) => {
  const { t, i18n } = useTranslation();

  const valueOf = (entry: PublicScanBrandVisibility) => entry.visibility;

  const ranked = useMemo(
    () => [...brands].sort((a, b) => valueOf(b) - valueOf(a)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brands]
  );
  const trackedIndex = useMemo(() => ranked.findIndex((entry) => entry.isTracked), [ranked]);
  const tracked = trackedIndex >= 0 ? ranked[trackedIndex] : undefined;
  const trackedLabel = tracked ? brandLabel(tracked.brand) : undefined;

  // Wykres oczekuje `PieChartData` — tego samego kształtu, którym karmi go panel.
  const data = useMemo(
    () => ranked.map((entry) => ({ name: brandLabel(entry.brand), value: valueOf(entry) })),
    [ranked]
  );

  // Kolory z mapy raportu — marka ma ten sam kolor tu, co na wykresie Share of Voice.
  const chartColors = useMemo(
    () =>
      data.map((entry, index) => ({
        name: entry.name,
        color: brandColorOf(brandColors, entry.name, entry.name === trackedLabel, index),
      })),
    [data, trackedLabel, brandColors]
  );

  // Slajd bez danych w ogóle nie wchodzi do raportu — ani na stronie, ani w PDF.
  if (ranked.length === 0) {
    return null;
  }

  const trackedColor = chartColors.find((entry) => entry.name === trackedLabel)?.color;

  return (
    <ReportSection
      title={t('reportRankingTitle', { rank: formatRank(trackedIndex + 1, i18n.language) })}
      titleAccent={t('reportRankingTitleAccent')}
      subtitle={t('reportRankingSubtitle', { brand: trackedLabel })}
      // Wykres do szerokiej kolumny, kafel marki zostaje pod tytułem — ten sam podział, co na
      // slajdzie Platform Performance.
      aside={
        <BrandVisibilityBarChart
          data={data}
          brandColors={chartColors}
          selectedBrand={trackedLabel}
          chartRef={chartRef}
          // Raport pokazuje wartości nad słupkami — jego PDF jest zrzutem tego wykresu.
          showValueLabels
        />
      }
    >
      {tracked && (
        <div className="report-ranking__brand" style={{ backgroundColor: trackedColor }}>
          <span className="report-ranking__brand-label">{t('reportRankingYourBrand')}</span>
          <span className="report-ranking__brand-value">
            <span className="report-ranking__brand-name" title={trackedLabel}>
              {trackedLabel}
            </span>
            <span className="report-ranking__brand-percent">{valueOf(tracked)}%</span>
          </span>
        </div>
      )}
    </ReportSection>
  );
};

export default ReportCompetitiveRanking;
